import React from "react";
import { useTranslation } from "react-i18next";
import { BreakdownIndicator, TrafficLight } from "./balancedScorecard.service";
import "./IndicatorBreakdown.css";

interface IndicatorBreakdownProps {
  dimensionName: string;
  categories: { id: string; name: string }[];
  /** undefined when the indicator is missing from the breakdown response. */
  indicator: BreakdownIndicator | undefined;
}

const TL_COLOR: Record<string, string> = {
  green: "#16a34a",
  yellow: "#d97706",
  red: "#dc2626",
};

/**
 * Per-category values for one indicator under "Breakdown". The colour comes
 * from the server's band (or traffic light) for that cell. The UI does not
 * score anything itself.
 */
const IndicatorBreakdown: React.FC<IndicatorBreakdownProps> = ({
  dimensionName,
  categories,
  indicator,
}) => {
  const { t } = useTranslation("balancedScorecard");
  if (!indicator || !indicator.disaggregated) {
    return (
      <div className="bsc-bd-note">
        {t("breakdown.noBreakdown", { dimension: dimensionName })}
      </div>
    );
  }

  const colorFor = (tl: TrafficLight, bandColor?: string) =>
    bandColor ?? (tl ? TL_COLOR[tl] : "#9ca3af");

  return (
    <table className="bsc-bd-table">
      <thead>
        <tr>
          <th>{dimensionName}</th>
          <th>{t("breakdown.value")}</th>
        </tr>
      </thead>
      <tbody>
        {categories.map((c) => {
          const cell = indicator.cells[c.id];
          // No cell or a null value means "no data", not zero.
          if (!cell || cell.value === null) {
            return (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td className="bsc-bd-empty">—</td>
              </tr>
            );
          }
          return (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td
                className="bsc-bd-value"
                style={{ color: colorFor(cell.traffic_light, cell.band?.color) }}
              >
                {Number(cell.value)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default IndicatorBreakdown;
